import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2, ShieldCheck, Ban, CheckCircle2, Loader2, ArrowRight, Info,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { getSourceMeta, submitSource, getMarkets } from "../lib/api";

const STATE_STEPS = [
  { key: "SUBMITTED", label: "Submitted", desc: "Request received" },
  { key: "UNDER_REVIEW", label: "Under review", desc: "Atlas evaluates the source" },
  { key: "VERIFIED", label: "Verified", desc: "Business confirmed legitimate" },
  { key: "ACTIVE", label: "Active", desc: "Included in Atlas results" },
];

const empty = {
  organization_name: "", website: "", country: "", source_type: "",
  inventory_url: "", contact_name: "", contact_email: "", contact_phone: "",
  contact_role: "", notes: "",
};

const Field = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="overline">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function Partners() {
  const [meta, setMeta] = useState({ source_types: [], disclaimer: "" });
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [accept, setAccept] = useState(false);

  useEffect(() => {
    getSourceMeta().then(setMeta).catch(() => {});
    getMarkets()
      .then((d) => setCountries((d?.markets || []).map((m) => m.name).sort()))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.organization_name.trim() &&
    form.website.trim() &&
    form.country.trim() &&
    form.source_type &&
    form.contact_name.trim() &&
    form.contact_email.trim();

  const canSubmit = valid && accept;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitSource({ ...form, accept_terms: true });
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError("We couldn't submit your source right now. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partners-page">
      {/* Hero */}
      <section className="bg-atlas-navy text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16">
          <div className="overline text-atlas-sky mb-3 flex items-center gap-2">
            <Building2 className="h-4 w-4" /> For dealers &amp; automotive businesses
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
            List your dealership.<br />
            <span className="text-gradient-blue">Request inclusion as an Atlas source.</span>
          </h1>
          <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">
            Atlas is an intelligence layer over Europe's fragmented vehicle market. Legitimate
            dealers, dealership groups and automotive websites can request that their inventory
            source be considered for inclusion. Atlas reviews every request individually.
          </p>
          <p className="text-slate-400 text-sm mt-4 flex items-start gap-2 max-w-2xl">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-atlas-sky" />
            Submission is a request for consideration only. It does not guarantee inclusion, and
            no payment or transaction ever occurs through Atlas.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 space-y-12">
        {/* Verification lifecycle */}
        <div>
          <h2 className="font-display text-2xl font-bold mb-5">How verification works</h2>
          <div className="grid sm:grid-cols-4 gap-3" data-testid="verification-lifecycle">
            {STATE_STEPS.map((s, i) => (
              <div key={s.key} className="relative border border-border rounded-md p-4 bg-card">
                <div className="font-mono text-[11px] text-atlas-blue mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-semibold text-sm">{s.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
                {i < STATE_STEPS.length - 1 && (
                  <ArrowRight className="hidden sm:block absolute top-1/2 -right-2.5 -translate-y-1/2 h-4 w-4 text-slate-300" />
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            A source may also be marked <span className="font-mono">SUSPENDED</span> at any time.
            Atlas retains full editorial and data control over verification and activation at
            every stage.
          </p>
        </div>

        {result ? (
          <div className="border-2 border-emerald-200 rounded-md p-6 md:p-8 bg-emerald-50/50" data-testid="submission-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <h2 className="font-display text-2xl font-bold">Source received</h2>
            </div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white border border-emerald-200">
              <span className="text-xs text-muted-foreground">Status</span>
              <span className="font-mono text-xs font-semibold text-emerald-700" data-testid="submission-status">
                {result.submission?.verification_status}
              </span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-4">{result.message}</p>
            <div className="text-sm text-slate-600 space-y-1 border-t border-emerald-200 pt-4">
              <div><span className="text-muted-foreground">Organization:</span> {result.submission?.organization_name}</div>
              <div><span className="text-muted-foreground">Source type:</span> {result.submission?.source_type}</div>
              <div><span className="text-muted-foreground">Country:</span> {result.submission?.country}</div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => { setResult(null); setForm(empty); }} data-testid="submit-another-btn" variant="outline">
                Submit another source
              </Button>
              <Button asChild className="bg-primary hover:bg-atlas-blue text-white">
                <Link to="/coverage">See market coverage</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="font-display text-2xl font-bold mb-1">Add your source</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Tell us about your business and where your inventory lives.
            </p>

            <div className="border border-border rounded-md p-6 md:p-8 bg-card space-y-8">
              <section>
                <h3 className="overline text-primary mb-4">Organization</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Dealer / organization name" required>
                    <Input data-testid="src-org-name" value={form.organization_name}
                      onChange={(e) => set("organization_name", e.target.value)} placeholder="e.g. AutoHaus Müller GmbH" />
                  </Field>
                  <Field label="Source type" required>
                    <Select value={form.source_type} onValueChange={(v) => set("source_type", v)}>
                      <SelectTrigger data-testid="src-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {meta.source_types.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Website" required hint="Your public business website.">
                    <Input data-testid="src-website" value={form.website}
                      onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
                  </Field>
                  <Field label="Country" required>
                    {countries.length ? (
                      <Select value={form.country} onValueChange={(v) => set("country", v)}>
                        <SelectTrigger data-testid="src-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {countries.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid="src-country-input" value={form.country}
                        onChange={(e) => set("country", e.target.value)} placeholder="e.g. Germany" />
                    )}
                  </Field>
                  <Field label="Inventory feed / URL" hint="Where your live stock is published (optional).">
                    <Input data-testid="src-inventory-url" value={form.inventory_url}
                      onChange={(e) => set("inventory_url", e.target.value)} placeholder="https://.../inventory" />
                  </Field>
                </div>
              </section>

              <section>
                <h3 className="overline text-primary mb-4">Contact</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Contact name" required>
                    <Input data-testid="src-contact-name" value={form.contact_name}
                      onChange={(e) => set("contact_name", e.target.value)} placeholder="Full name" />
                  </Field>
                  <Field label="Role" hint="Your role at the business (optional).">
                    <Input data-testid="src-contact-role" value={form.contact_role}
                      onChange={(e) => set("contact_role", e.target.value)} placeholder="e.g. Sales Director" />
                  </Field>
                  <Field label="Email" required>
                    <Input data-testid="src-contact-email" type="email" value={form.contact_email}
                      onChange={(e) => set("contact_email", e.target.value)} placeholder="name@business.com" />
                  </Field>
                  <Field label="Phone" hint="Optional.">
                    <Input data-testid="src-contact-phone" value={form.contact_phone}
                      onChange={(e) => set("contact_phone", e.target.value)} placeholder="+49 ..." />
                  </Field>
                </div>
              </section>

              <section>
                <Field label="Anything else we should know?">
                  <Textarea data-testid="src-notes" value={form.notes} rows={3}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Approx. inventory size, brands you specialise in, data format, etc." />
                </Field>
              </section>

              {error && (
                <p className="text-sm text-red-600" data-testid="submit-error">{error}</p>
              )}

              <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
                <Checkbox id="dealer-terms" checked={accept} onCheckedChange={(v) => setAccept(!!v)}
                  data-testid="src-accept-terms" className="mt-0.5" />
                <label htmlFor="dealer-terms" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                  I am authorised to represent this business and accept the{" "}
                  <Link to="/legal/terms" target="_blank" className="text-atlas-blue font-medium">dealer/source submission terms</Link>.
                  I understand submission does not guarantee inclusion and that Atlas retains full control over verification.
                </label>
              </div>

              <div className="flex items-center gap-4">
                <Button onClick={submit} disabled={!canSubmit || submitting}
                  data-testid="src-submit-btn"
                  className="bg-primary hover:bg-atlas-blue text-white px-8 py-6 text-base">
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
                  ) : (
                    <>Submit for consideration <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Fields marked <span className="text-red-500">*</span> are required.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No-payment / no-guarantee notice */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border-2 border-atlas-blue/30 rounded-md p-6 bg-blue-50/40">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5 text-atlas-blue" />
              <h3 className="font-display text-lg font-semibold">Atlas stays in control</h3>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {meta.disclaimer ||
                "Submission is a request for consideration only. Atlas retains full editorial and data control over whether a source is verified and activated. Submission does not guarantee inclusion."}
            </p>
          </div>
          <div className="border border-border rounded-md p-6 bg-card">
            <div className="flex items-center gap-2 mb-3 text-red-700">
              <Ban className="h-5 w-5" />
              <h3 className="font-display text-lg font-semibold">No payments, ever</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Atlas is not a marketplace and never processes payments. Users cannot buy a vehicle
              or pay through Atlas — the platform only links to original listings. No listing,
              placement or verification fee is charged to submit a source.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
